<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen"
                 @click.self="dismiss()"
                 class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 z-[51]"
            >
                <div class="jgm-scroll max-w-lg w-full bg-white rounded-3xl shadow-xl overflow-hidden relative max-h-[90vh] overflow-y-auto">

                    <!-- Close Button -->
                    <button
                        @click="dismiss()"
                        class="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/30 transition-colors"
                    >
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <!-- Header + pricing/option section share /gold's own dark hero
                         backdrop (naGoldNew.blade.php's .g-pricing: a heavily
                         grayscaled + darkened photo, plus a soft gold glow) rather
                         than this modal's plain white background, so the member
                         recognizes the same "Experience Gold Today" moment
                         wherever it's promoted from. See .jgm-hero-* below. -->
                    <div class="jgm-hero">
                        <div class="jgm-hero-bg" aria-hidden="true"></div>
                        <div class="jgm-hero-overlay" aria-hidden="true"></div>
                        <div class="jgm-hero-glow" aria-hidden="true"></div>

                        <div class="jgm-hero-content">
                        <!-- Header Section -->
                        <div class="px-8 pt-8 pb-6 text-center">
                            <!-- Same heading + styling as /gold's own pricing heading
                                 (naGoldNew.blade.php's #g-pricing-heading /
                                 .g-pricing__heading-gold): "Gold" rendered in the same
                                 gold-gradient text-clip, "Experience"/"Today" in white. -->
                            <h2 class="jgm-hero-heading">
                                Experience <span class="jgm-hero-heading-gold">Gold</span> Today
                            </h2>
                            <p class="text-sm mt-2" style="color: rgba(255, 255, 255, 0.7);">Double your Star Rewards</p>
                        </div>

                        <!-- Lifetime GOLD (PricingHelper::getLifetimeGoldOption()) is what this
                             modal promotes — a single one-time payment, no recurring plan to
                             pick between. Falls back to the Annual/Monthly picker below only
                             when Lifetime isn't a real option for this member (see
                             lifetimeGoldOption's doc). -->
                        <template v-if="lifetimeGoldOption">
                        <!-- Mirrors /gold's own Plus Gold pricing card exactly
                             (naGoldNew.blade.php's .g-plan--annual.g-plan--solo,
                             the "pricing_section" placement) rather than this
                             modal's own light card style, so the member sees the
                             identical offer whichever surface promoted it: same
                             translucent gold-glass card (rgba(212,175,55,0.07),
                             not a solid dark fill), floating crown badge, big white
                             price, gold gradient CTA, and the same billing note
                             copy — crown_label/period/billing_note all come from
                             PricingHelper::getLifetimeGoldOption(), including its
                             recurring-vs-one-time branch, so this can't drift
                             from /gold's own copy. No perks bullet list here,
                             matching that card's actual (sparser) markup. -->
                        <div class="px-6 pt-2 pb-3 space-y-3">
                            <div
                                class="relative rounded-2xl px-6 pt-9 pb-6 text-center"
                                style="background: rgba(212, 175, 55, 0.07); border: 1px solid rgba(212, 175, 55, 0.4); box-shadow: 0 0 60px rgba(212, 175, 55, 0.08), 0 24px 60px rgba(0, 0, 0, 0.5);"
                            >
                                <span
                                    class="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3.5 py-1 text-[10px] font-extrabold uppercase text-black"
                                    style="letter-spacing: 2px; background: linear-gradient(90deg, #b8860b, #f0d078, #c9a84c); z-index: 5;"
                                >
                                    {{ lifetimeGoldOption.data.crown_label }}
                                </span>

                                <div class="flex items-baseline justify-center gap-0.5 mb-1">
                                    <span class="text-lg font-bold" style="color: rgba(255, 255, 255, 0.7);">$</span>
                                    <span class="text-5xl font-extrabold text-white" style="letter-spacing: -2px;">{{ lifetimePriceParts.dollars }}</span>
                                    <span class="text-xl font-bold" style="color: rgba(255, 255, 255, 0.7);">.{{ lifetimePriceParts.cents }}</span>
                                </div>
                                <p class="text-xs uppercase mb-5" style="letter-spacing: 1.2px; color: rgba(255, 255, 255, 0.45);">{{ lifetimeGoldOption.data.period }}</p>

                                <button
                                    @click="handleJoin"
                                    class="block w-full text-center py-4 px-6 rounded-[10px] text-sm font-extrabold uppercase text-black mt-3 transition"
                                    style="letter-spacing: 1.5px; background: linear-gradient(135deg, #b8860b 0%, #f0d078 50%, #c9a84c 100%); box-shadow: 0 10px 32px rgba(212, 175, 55, 0.4);"
                                >
                                    Upgrade to Gold
                                </button>
                                <p class="text-[10px] mt-3" style="color: rgba(255, 255, 255, 0.4);">
                                    {{ lifetimeGoldOption.data.billing_note }}
                                </p>
                            </div>
                        </div>
                    </template>

                        <template v-else>
                        <!-- Join Options -->
                        <div class="px-6 pt-2 pb-3 space-y-3">
                            <button
                                v-for="option in joinOptions"
                                :key="option.name"
                                @click="selectOption(option.name, option.type)"
                                :class="[
                                    'relative w-full p-5 rounded-2xl border-2 transition-all text-left hover:shadow-2xl active:scale-[0.99]',
                                    selectedOption === option.name
                                        ? 'border-amber-500 bg-gray-900 ring-4 ring-amber-500/20'
                                        : 'border-gray-200 bg-white opacity-70 hover:opacity-100'
                                ]"
                            >
                                <div v-if="option.name === 'Annual'" class="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full shadow-lg">
                                    BEST VALUE • SAVE 38%
                                </div>

                                <div class="flex items-center justify-between mt-1">
                                    <div class="flex-1">
                                        <div class="flex items-baseline gap-1 mb-2">
                                            <span :class="['text-5xl font-bold', selectedOption === option.name ? 'text-white' : 'text-gray-900']">
                                                {{ option.dailyDisplayPrice }}
                                            </span>
                                            <span :class="['text-lg', selectedOption === option.name ? 'text-gray-300' : 'text-gray-600']">
                                                /day
                                            </span>
                                        </div>
                                        <div :class="['text-xs font-semibold uppercase tracking-wide mb-2', selectedOption === option.name ? 'text-gray-400' : 'text-gray-500']">
                                            {{ option.name }}
                                        </div>

                                        <div class="space-y-1.5">
                                            <div v-for="content in option.includedContent" :key="content" class="flex items-center gap-1.5">
                                                <svg :class="['w-3.5 h-3.5 flex-shrink-0', selectedOption === option.name ? 'text-amber-500' : 'text-gray-400']" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                                </svg>
                                                <span :class="['text-xs', selectedOption === option.name ? 'text-white' : 'text-gray-900']">
                                                    {{ content }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </button>

                            <!-- Billing Details -->
                            <div class="text-center pt-2 pb-1">
                                <p class="text-xs text-white/60">
                                    Annual plan billed at {{ yearlyTotalPrice }}/year • Monthly plan billed at {{ monthlyTotalPrice }}/month
                                </p>
                            </div>

                            <!-- CTA Button -->
                            <button
                                @click="handleJoin"
                                class="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-[0.99] mt-2"
                            >
                                Upgrade to GOLD
                            </button>
                        </div>
                        </template>
                        </div>
                    </div>

                    <!-- Bonus Sites Section -->
                    <div class="px-6 pt-6 pb-6">
                        <div class="mb-3">
                            <div class="text-xs font-semibold text-gray-900 mb-1">Included with every plan</div>
                            <!-- bonusSites.length, not the "11 total sites" figure used
                                 elsewhere (config('gold-sites.total_sites')) — this grid
                                 only shows the bonus sites, not Naughty America itself,
                                 so the count here must match what's actually rendered. -->
                            <div class="text-xs text-gray-500">Unlimited streaming on {{ bonusSites.length }} bonus sites</div>
                        </div>

                        <div class="grid grid-cols-2 gap-2 mb-4">
                            <div
                                v-for="site in bonusSites"
                                :key="site.name"
                                class="flex items-center justify-center p-3 bg-gray-50 rounded-lg h-14"
                            >
                                <img :src="site.logo" :alt="site.name" class="max-h-6 max-w-[85%] object-contain">
                            </div>
                        </div>

                        <!-- Trust Indicators -->
                        <div class="flex items-center justify-center gap-3 text-xs text-gray-500 pt-4">
                            <span class="flex items-center gap-1">
                                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                                </svg>
                                Secure Payment
                            </span>
                            <span class="text-gray-300">•</span>
                            <span>Instant Access</span>
                            <span class="text-gray-300">•</span>
                            <span>No Commitment</span>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {ref, defineEmits, computed, watch} from 'vue';
import {usePage} from "@inertiajs/vue3";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";
import {trackEvent} from "@/Helpers/analytics";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    source: {
        type: String,
        default: ''
    },
    audition: {
        type: Object,
        default: null
    },
    // Interstitial ad creative id (InterstitialAdModal.vue's creative.id) —
    // appended as psc5 attribution on every purchase URL below (see
    // appendGoldAttribution) so a GOLD conversion driven through this modal
    // stays linkable back to the specific ad/creative that opened it, even
    // when the click is short-circuted into the Plus Gold one-time flow
    // (AccountTrait::performGoldUpgrade forwards psc3/4/5 from the incoming
    // request when present — see that method's doc comment). Null for any
    // other JoinGoldModal caller (e.g. a future non-ad trigger).
    creativeId: {
        type: String,
        default: null
    },
    /**
     * Free-form props merged into every event this modal fires (placement,
     * product_line, trigger, membership_* ...). Mirrors the Blade twin's
     * third openJoinGoldModal() argument, so a new caller becomes reportable
     * by passing a source — nothing here is required.
     */
    context: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['close'])

const selectedOption = ref('Annual')
const joinOptionsData = usePage().props.joinOptions

const joinOptions = computed(() => {
    const options = []
    if (joinOptionsData.button_yearlyGold) {
        options.push({
            name: joinOptionsData.button_yearlyGold.data.name,
            stars: joinOptionsData.button_yearlyGold.data.stars,
            stars_text: joinOptionsData.button_yearlyGold.data.stars_text,
            dailyDisplayPrice: `$${joinOptionsData.button_yearlyGold.data.formatted_daily_amount}`,
            fullDisplayPrice: `$${joinOptionsData.button_yearlyGold.data.formatted_initial_amount}`,
            period: joinOptionsData.button_yearlyGold.data.period,
            description: joinOptionsData.button_yearlyGold.data.included_content,
            includedContent: joinOptionsData.button_yearlyGold.data.included_content,
            url: joinOptionsData.button_yearlyGold.url,
            type: 'yearlyGold'
        })
    }
    if (joinOptionsData.button_monthlyGold) {
        options.push({
            name: joinOptionsData.button_monthlyGold.data.name,
            stars: joinOptionsData.button_monthlyGold.data.stars,
            stars_text: joinOptionsData.button_monthlyGold.data.stars_text,
            dailyDisplayPrice: `$${joinOptionsData.button_monthlyGold.data.formatted_daily_amount}`,
            fullDisplayPrice: `$${joinOptionsData.button_monthlyGold.data.formatted_initial_amount}`,
            period: joinOptionsData.button_monthlyGold.data.period,
            description: joinOptionsData.button_monthlyGold.data.included_content,
            includedContent: joinOptionsData.button_monthlyGold.data.included_content,
            url: joinOptionsData.button_monthlyGold.url,
            type: 'monthlyGold'
        })
    }

    return options;
})
const selectedOptionData = computed(() =>
    joinOptions.value.find((t) => t.name === selectedOption.value)
)

// Lifetime GOLD (PricingHelper::getLifetimeGoldOption()) is what this modal
// promotes now — null whenever it isn't a real option for this member
// (feature flag off, no active SKU, or PlusGoldHelper eligibility fails),
// in which case the template falls back to the recurring Annual/Monthly
// picker above rather than showing nothing.
const lifetimeGoldOption = computed(() => joinOptionsData.button_lifetimeGold ?? null)

// Split "199.95" into {dollars: '199', cents: '95'} for the big-integer/
// smaller-decimals price treatment matching /gold's .g-plan__amount +
// .g-plan__decimals (naGoldNew.blade.php).
const lifetimePriceParts = computed(() => {
    const amount = lifetimeGoldOption.value?.data?.formatted_initial_amount ?? '0.00'
    const [dollars, cents] = amount.split('.')
    return { dollars, cents: cents ?? '00' }
})
// button_yearlyGold/button_monthlyGold can each independently be null —
// PricingHelper::getFeedJoinOptions() sets a button to null rather than
// throwing when its own pricing lookup fails, same as the joinOptions
// computed above already accounts for via its `if (joinOptionsData.button_*)`
// guards. Optional-chained here too so a single failed lookup doesn't crash
// this modal entirely.
const yearlyTotalPrice = computed(() => `$${joinOptionsData.button_yearlyGold?.data.formatted_initial_amount ?? '—'}`)
const monthlyTotalPrice = computed(() => `$${joinOptionsData.button_monthlyGold?.data.formatted_initial_amount ?? '—'}`)

/**
 * Appends psc3/psc4/psc5 attribution onto a GOLD purchase URL so whichever
 * button the member clicks stays linkable back to the ad/creative that
 * opened this modal — mirrors PlusGoldHelper::attributionQuery()'s slot
 * semantics (psc3 = placement, psc4 = source page, psc5 = sub-element).
 * Lifetime's URL already carries psc3/psc4 baked in server-side (see
 * PricingHelper::getLifetimeGoldOption()) — this only ADDS psc5 there,
 * never overwriting what's already set. Annual/Monthly's URL has no psc
 * attribution at all until this runs; AccountTrait::performGoldUpgrade
 * forwards whatever it finds here into its own Plus Gold redirect when it
 * takes that branch (the common case whenever Plus Gold is enabled for
 * this member), which is what actually fires for most members clicking
 * any of the three buttons — see that method's doc comment.
 *
 * No-ops (returns url unchanged) when creativeId is null — i.e. whenever
 * this modal wasn't opened from an interstitial ad.
 */
/**
 * Whether the member is on their way to the biller, so the modal closing is
 * not reported as a dismissal. Mirrors the Blade twin's jgmJoining.
 */
const joining = ref(false)

/**
 * Props shared by every event this modal fires — see the `context` prop.
 * `extra` is per-event detail and wins over the caller's context.
 */
function goldModalEventProps(extra = {}) {
    return {
        source: props.source,
        creative_id: props.creativeId,
        // Needed for unique-member counts: the funnel pipes count
        // uniqExactIf(user_id). Read off Inertia's shared auth prop rather
        // than required from every caller, and still overridable via context.
        user_id: usePage().props.auth?.user?.id ?? null,
        ...props.context,
        ...extra,
    }
}

// The denominator for the whole modal funnel: an ad click that opened this
// and went nowhere used to look identical to one that reached the biller.
watch(() => props.isOpen, (isOpen) => {
    if (!isOpen) {
        return
    }

    joining.value = false
    trackEvent('Join Gold Modal Shown', goldModalEventProps())
})

function selectOption(optionName, joinType) {
    selectedOption.value = optionName
    trackEvent('Join Gold Option Clicked', goldModalEventProps({ join_type: joinType ?? optionName }))
}

function dismiss() {
    if (!joining.value) {
        trackEvent('Join Gold Modal Dismissed', goldModalEventProps())
    }

    emit('close')
}

/**
 * Appends psc3/psc4/psc5 attribution onto a GOLD purchase URL — the twin of
 * join_gold_modal.blade.php's jgmAppendGoldAttribution(), and kept identical
 * to it deliberately.
 *
 * Gated on `source`, not creativeId: a non-ad caller has no creative but
 * still needs psc4 stamped, and psc5 is the only piece that genuinely
 * depends on there being a creative.
 *
 * psc4 is always overwritten rather than set-if-absent. The Lifetime CTA's
 * URL arrives with a generic psc4=joinGoldModal already baked in
 * server-side (HandleInertiaRequests passes 'joinGoldModal' to
 * PricingHelper::getLifetimeGoldOption), so set-if-absent silently swallowed
 * the real source — an ad-driven Lifetime GOLD purchase from a Vue surface
 * reported as the modal itself and fell out of
 * interstitial_ad_gold_conversions.pipe, which matches psc4 on the
 * Interstitial_Ad_ prefix. The Blade twin was fixed for this on 2026-08-17;
 * this side was missed.
 *
 * psc3 stays the constant 'join_gold_modal' UI-mechanism marker (set only if
 * absent) — it describes the modal, not who opened it.
 */
function appendGoldAttribution(url) {
    if (!props.source) {
        return url
    }

    const parsed = new URL(url, window.location.origin)
    if (!parsed.searchParams.has('psc3')) {
        parsed.searchParams.set('psc3', 'join_gold_modal')
    }
    parsed.searchParams.set('psc4', props.source)
    if (props.creativeId) {
        parsed.searchParams.set('psc5', props.creativeId)
    }
    return parsed.toString()
}

function redirectToJoin(joinUrl, joinType) {
    console.log('++ JoinOptions: From ' + props.source + ' Redirecting to', joinUrl);

    // Suppresses the dismissal event — the modal goes away here as a
    // conversion step, not an abandonment.
    joining.value = true;
    localStorage.setItem('blue_join_options_source', props.source);
    localStorage.setItem('blue_join_options_join_type', joinType);
    localStorage.setItem('blue_join_options_url', joinUrl);

    if (props.audition) {

        LTIAnalytics.setCookie("gold_upgrade_source", props.source);
        LTIAnalytics.setCookie("gold_upgrade_video_id", props.audition.id ?? '');
        trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
            store_in_local_storage: true,
            local_storage_key: 'blue_join_options_video_props',
            append_data: goldModalEventProps({ join_type: joinType })
        });
    } else {
        // Carries creative_id and the opener's context now, so this step can
        // be joined to the ad that drove it. Additive for its existing
        // consumer (na_create_pitch_funnel.pipe in ancms).
        trackEvent('Biller Join Button Clicked', goldModalEventProps({ join_type: joinType }));
    }

    window.location.href = joinUrl;
}

const handleJoin = () => {
    // Lifetime GOLD's URL already carries its own psc2/psc3/psc4/psc6
    // attribution (see PlusGoldHelper::purchaseHrefForPlacement()) — unlike
    // the recurring options below, it doesn't need a custom8 param appended.
    // appendGoldAttribution still runs, and its psc4 OVERWRITE is what matters
    // here: the psc4 baked into that URL is the generic 'joinGoldModal', not
    // whoever opened this modal.
    if (lifetimeGoldOption.value) {
        redirectToJoin(appendGoldAttribution(lifetimeGoldOption.value.url), 'lifetimeGold');
        return;
    }

    const joinUrl = selectedOptionData.value.url +
        (selectedOptionData.value.url.includes('?') ?
            `&custom8=${props.source}` :
            `?custom8=${props.source}`)

    redirectToJoin(appendGoldAttribution(joinUrl), selectedOptionData.value.type);
}

// Same 10 sites, logos, and display order as /joinPage's own "View all 11
// sites" gold modal (GoldController::buildGoldSitesForMa(), shared here as
// goldSites.bonus_sites_ma — see HandleInertiaRequests) — reused rather than
// a separately maintained list, so this grid can't drift from what members
// already see promoted there. `logo` is a fully-resolved asset() URL.
const bonusSites = computed(() => usePage().props.goldSites?.bonus_sites_ma ?? [])
</script>

<style scoped>
/* Thin gold scrollbar for the modal's own scroll container, matching the
   gold palette used throughout (crown badge / CTA gradient: #b8860b →
   #f0d078 → #c9a84c). Firefox via scrollbar-width/-color, WebKit browsers
   (Chrome/Safari/Edge) via the ::-webkit-scrollbar pseudo-elements. */
.jgm-scroll {
    scrollbar-width: thin;
    scrollbar-color: #c9a84c transparent;
}
.jgm-scroll::-webkit-scrollbar {
    width: 6px;
}
.jgm-scroll::-webkit-scrollbar-track {
    background: transparent;
}
.jgm-scroll::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #b8860b, #f0d078, #c9a84c);
    border-radius: 999px;
}
.jgm-scroll::-webkit-scrollbar-thumb:hover {
    background: #c9a84c;
}

/* Same dark hero treatment as /gold's .g-pricing section (naGoldNew.blade.php):
   a heavily grayscaled + darkened photo behind a soft gold radial glow, so
   the header + pricing/option area reads as the same moment regardless of
   which surface promoted GOLD. Layered as real elements (not ::before/::after)
   since Vue's scoped styles don't need the pseudo-element trick /gold uses. */
.jgm-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
}
.jgm-hero-bg {
    position: absolute;
    inset: -10%;
    background: url('/images/gold/footer_tngf.webp') center center / cover no-repeat;
    filter: grayscale(100%);
    z-index: 0;
}
.jgm-hero-overlay {
    position: absolute;
    inset: 0;
    background: rgba(8, 8, 8, 0.82);
    z-index: 1;
}
.jgm-hero-glow {
    position: absolute;
    top: 10%;
    left: 50%;
    width: min(100%, 480px);
    height: 260px;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(184, 134, 11, 0.28) 0%, rgba(184, 134, 11, 0.1) 35%, rgba(184, 134, 11, 0) 72%);
    filter: blur(50px);
    z-index: 2;
}
.jgm-hero-content {
    position: relative;
    z-index: 3;
}

/* Matches /gold's #g-pricing-heading + .g-pricing__heading-gold exactly:
   "Gold" gets the gold-gradient text-clip treatment, the rest stays white. */
.jgm-hero-heading {
    margin: 0;
    font-size: clamp(1.75rem, 6vw, 2.5rem);
    font-weight: 800;
    line-height: 0.95;
    letter-spacing: -0.02em;
    color: #fff;
}
.jgm-hero-heading-gold {
    background: linear-gradient(90deg, #b8860b 0%, #f0d078 48%, #c9a84c 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .max-w-md,
.modal-leave-active .max-w-md {
    transition: transform 0.3s ease;
}

.modal-enter-from .max-w-md,
.modal-leave-to .max-w-md {
    transform: scale(0.95);
}

/* Override text-sm to be 12px instead of default 14px */
.text-xs {
    font-size: 12px;
}
</style>
